import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { loadStripe, Stripe, StripeElements, StripeCardNumberElement, StripeCardExpiryElement, StripeCardCvcElement } from '@stripe/stripe-js';
import { environment } from 'environments/environment';
import { finalize } from 'rxjs';
import { AlertService } from 'app/components/ui/alert/alert.service';
import { BillingService } from 'app/services/billing/billing.service';

@Component({
    selector: 'add-card-modal',
    templateUrl: './add-card-modal.component.html',
    styleUrls: ['./add-card-modal.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatProgressSpinnerModule
    ]
})
export class AddCardModalComponent implements OnInit, OnDestroy {
    private stripe: Stripe;
    private elements: StripeElements;
    private cardNumber: StripeCardNumberElement;
    private cardExpiry: StripeCardExpiryElement;
    private cardCvc: StripeCardCvcElement;

    cardName: string = '';
    setAsDefault: boolean = false;
    loading: boolean = false;

    cardNumberError: string = '';
    cardExpiryError: string = '';
    cardCvcError: string = '';

    private cardNumberComplete: boolean = false;
    private cardExpiryComplete: boolean = false;
    private cardCvcComplete: boolean = false;

    constructor(
        public dialogRef: MatDialogRef<AddCardModalComponent>,
        private _alertService: AlertService,
        private _billingService: BillingService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.initializeStripe();
    }

    private async initializeStripe(): Promise<void> {
        // Substitua pela sua chave pública do Stripe
        this.stripe = await loadStripe(environment.config.stripePublicKey);

        if (!this.stripe) {
            console.error('Stripe failed to initialize');
            return;
        }

        this.elements = this.stripe.elements();

        const style = {
            base: {
                fontSize: '16px',
                color: '#424242',
                fontFamily: '"Inter", sans-serif',
                '::placeholder': {
                    color: '#9e9e9e'
                }
            },
            invalid: {
                color: '#f44336'
            }
        };

        // Criar elementos
        this.cardNumber = this.elements.create('cardNumber', { style });
        this.cardExpiry = this.elements.create('cardExpiry', { style });
        this.cardCvc = this.elements.create('cardCvc', { style });

        // Montar nos containers
        this.cardNumber.mount('#card-number-element');
        this.cardExpiry.mount('#card-expiry-element');
        this.cardCvc.mount('#card-cvc-element');

        // Listeners de mudança e erro
        this.cardNumber.on('change', (event) => {
            this.cardNumberError = event.error?.message || '';
            this.cardNumberComplete = event.complete;
        });

        this.cardExpiry.on('change', (event) => {
            this.cardExpiryError = event.error?.message || '';
            this.cardExpiryComplete = event.complete;
        });

        this.cardCvc.on('change', (event) => {
            this.cardCvcError = event.error?.message || '';
            this.cardCvcComplete = event.complete;
        });
    }

    isFormValid(): boolean {
        return this.cardNumberComplete &&
               this.cardExpiryComplete &&
               this.cardCvcComplete &&
               this.cardName.trim().length > 0;
    }

    async addCard(): Promise<void> {
        if (!this.isFormValid() || this.loading) {
            return;
        }

        this.loading = true;

        const { paymentMethod, error } = await this.stripe.createPaymentMethod({
            type: 'card',
            card: this.cardNumber,
            billing_details: {
                name: this.cardName.toUpperCase()
            }
        });

        if (error) {
            console.error('Stripe error:', error);
            this.cardNumberError = error.message || 'Erro ao processar cartão';
            this.loading = false;
            return;
        }

        this._billingService.addPaymentMethod({
            payment_method_id: paymentMethod.id,
            set_as_default: this.setAsDefault
        })
        .pipe(finalize(() => this.loading = false))
        .subscribe({
            next: (response: any) => {
                this._alertService.success(response.message, { autoClose: true });
                this.dialogRef.close({ action: 'saved', paymentMethod });
            },
            error: (response) => {
                this._alertService.error(response.error.message, { autoClose: true });
            },
            complete: () => {

            }
        });
    }

    closeModal(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.cardNumber?.destroy();
        this.cardExpiry?.destroy();
        this.cardCvc?.destroy();
    }
}
